import {
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import styled from 'styled-components'

import CaptionText from 'features/PhotoEdit/CaptionText/CaptionText'
import { useLongPress } from 'helpers/hooks/useLongPress'
import { type IFile } from 'types/interfaces/IFile'

import { type IProps } from './PostImage.types'

const TARGET_MAX_CROP = 0.26

export const PostImage = ({
  fileIndex,
  allFiles,
  file,
  handleFileClick,
  onOpenOptions,
}: IProps & { onOpenOptions?: () => void }) => {
  const src =
    file.thumb_url ??
    file.url ??
    ''

  const containerRef =
    useRef<HTMLDivElement | null>(null)

  const [containerRatio, setContainerRatio] =
    useState(0)

  /*
   * Portrait media now uses a fixed rendered height
   * rather than a fixed CSS aspect ratio.
   *
   * Because the width remains responsive, we measure
   * the actual rendered container ratio so the crop
   * calculation always matches what is on screen.
   */
  useLayoutEffect(() => {
    const container =
      containerRef.current

    if (!container) return

    const measure = () => {
      const width =
        container.clientWidth

      const height =
        container.clientHeight

      if (!width || !height) return

      const nextRatio =
        width / height

      setContainerRatio(previous =>
        Math.abs(previous - nextRatio) > 0.001
          ? nextRatio
          : previous
      )
    }

    measure()

    const observer =
      new ResizeObserver(measure)

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [])

  const hasVideo = allFiles.some(
    (item: IFile) =>
      item.url.includes('/video') ||
      item.type === 'video'
  )

  const isPortrait =
    file.orientation === 'Portrait'

  const isLandscape =
    file.orientation === 'Landscape'

  const imageWidth =
    Number(file.width ?? 0)

  const imageHeight =
    Number(file.height ?? 0)

  const imageRatio =
    imageWidth > 0 &&
    imageHeight > 0
      ? imageWidth / imageHeight
      : 0

  /*
   * Normal behavior is object-fit: cover.
   *
   * We allow up to TARGET_MAX_CROP of horizontal
   * source loss.
   *
   * Once cover would crop more than that, the
   * foreground viewport becomes narrower instead.
   * The blurred copy then fills the exposed sides.
   */
  let adaptivePortrait = false
  let foregroundWidth = 1

  if (
    isPortrait &&
    !hasVideo &&
    imageRatio > 0 &&
    containerRatio > 0 &&
    imageRatio < containerRatio
  ) {
    const visibleFraction =
      imageRatio /
      containerRatio

    const cropFraction =
      1 - visibleFraction

    if (
      cropFraction >
      TARGET_MAX_CROP
    ) {
      foregroundWidth =
        Math.min(
          1,
          visibleFraction /
            (1 - TARGET_MAX_CROP)
        )

      adaptivePortrait = true
    }
  }

  const orientationClass =
    isPortrait
      ? 'post__files__file--portrait'
      : isLandscape
        ? 'post__files__file--landscape'
        : ''

  const hasPortraitImage =
    allFiles.some(
      (item: IFile) =>
        item.orientation === 'Portrait'
    )

  const isLandscapeInPortraitPost =
    !hasVideo &&
    !isPortrait &&
    hasPortraitImage

  const longPressHandlers =
    useLongPress({
      delay: 500,
      moveThreshold: 20,
      onLongPress: () => {
        onOpenOptions?.()
      },
    })

  return (
    <Container
      ref={containerRef}
      key={fileIndex}
      className={[
        'post__files__file',

        !hasVideo
          ? 'post__files__file--img'
          : '',

        !isLandscapeInPortraitPost
          ? orientationClass
          : '',

        !hasVideo &&
        isPortrait &&
        adaptivePortrait
          ? 'post__files__file--adaptive-portrait'
          : '',

        isLandscapeInPortraitPost
          ? 'post__files__file--landscape-in-portrait-post'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...longPressHandlers}
      $foregroundWidth={
        foregroundWidth
      }
      onClick={
        handleFileClick
      }
    >
      {(
        adaptivePortrait ||
        isLandscapeInPortraitPost
      ) && (
        <img
          src={src}
          alt=''
          aria-hidden='true'
          draggable={false}
          className='post-image__blur'
        />
      )}

      <div className='post-image__viewport'>
        <img
          src={src}
          alt='Post file'
          loading='lazy'
          draggable={false}
          className='post-image__main'
          onError={() => {
            console.log(
              `[IMG ERROR] now=${Date.now()} src=${src}`
            )
          }}
        />

        {file.text?.value ? (
          <CaptionText
            text={`${file.text.value}`}
            xPercent={
              file.text.x ?? 50
            }
            yPercent={
              file.text.y ?? 50
            }
          />
        ) : null}
      </div>
    </Container>
  )
}

const Container = styled.div<{
  $foregroundWidth: number
}>`
  --portrait-foreground-width: ${({
    $foregroundWidth,
  }) =>
    `${$foregroundWidth * 100}%`};

  -webkit-touch-callout: none;
  user-select: none;

  touch-action: pan-y;
`